import { h, Fragment } from "preact";
import { Typography, Modal, Input, Select, Button, Skeleton, message } from "antd";
import {
  UserOutlined,
  PlusCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "preact/hooks";
import FloatLabel from "./FloatLabel";
import axios from "axios";
import useProfile from "./useProfile";
import TeamActivityGraph from "./TeamActivityGraph";
import './TeamInfoTab.css';

export default function TeamInfoTab() {
  const { personKeycloak, person, teammates, addUser, deleteUser, options, updateUser, isLoading, profile } = useProfile();
  const [fullName, setFullName] = useState(null);
  const [position, setPosition] = useState(null);
  const [email, setEmail] = useState(null);
  const [role, setRole] = useState(null);
  const [personRole, setPersonRole] = useState(null);
  const [userToChangeId, setUserToChangeId] = useState(null);
  const [userToChangeRole, setUserToChangeRole] = useState(null);
  const [groupUsername, setGroupUsername] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [dateCreated, setDateCreated] = useState();

  const [modalMode, setModalMode] = useState("add"); // 'add' or 'edit'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInviteOrDeleteModalOpen, setIsInviteOrDeleteModalOpen] = useState(false);
  const [modalPurpose, setModalPurpose] = useState(null); // 'delete' or 'invite'

  const [messageApi, resultMessage] = message.useMessage();
  const [rolesCount, setRolesCount] = useState(null);

  const allowedRoles = new Set(options.map(option => option.value));

  const countRoles = () => {
    const realmRoles = {};
    teammates.forEach((teammate) => {
      teammate.roles.forEach((role) => {
        if (allowedRoles.has(role.name)) {
          realmRoles[role.name] = (realmRoles[role.name] || 0) + 1;
        }
      });
    });
    setRolesCount(realmRoles);
  };

  const roleHierarchy = {
    "super-admin": 4,
    "admin": 3,
    "operator": 2,
  };

  const handleUpdate = () => {
    updateUser(userToChangeId, fullName, position, email, role, userToChangeRole);
    setIsModalOpen(false);
  }
  const showModal = (mode, teammate) => {
    populateModalData(mode, teammate);
    setIsModalOpen(true);
  };

  const populateModalData = (mode, teammate) => {
    setModalMode(mode);
    const firstName = teammate.firstName == undefined || teammate.firstName == 'undefined' ? " " : teammate.firstName;
    const lastName = teammate.lastName == undefined || teammate.lastName == 'undefined' ? " " : teammate.lastName;
    const fullName = (firstName.trim() + " " + (lastName ? lastName.trim() : "")).trim();
    const teammatePosition = teammate?.attributes?.position?.[0] ?? "";
    setFullName(fullName);
    setPosition(teammatePosition);
    setEmail(teammate.email);
    let teammateRole = getRoleFromUser(teammate);
    setRole(teammateRole);
    setUserToChangeId(teammate.id);
    setUserToChangeRole(getRoleFromUser(teammate));
  };

  const addTeammate = async() => {
      const result = await addUser(fullName, email, position, role);
      if (result === true) {
        showInviteOrDeleteModal("invite");
        messageApi.open({
          type: 'success',
          content: 'User was created!',
        });
      } else {
        let error_message;
        switch(result){
          case 'User exists with same username':
            error_message = 'User exists with same email';
            break;
          case 'error-invalid-email':
            error_message = 'Invalid email'
            break;
          default:
            error_message = result;
            break;
        }
        messageApi.open({
          type: 'error',
          content: `Error! ${error_message}.`,
        });
      }
  };
  //not best practice, but to
  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const deleteTeammate = async() => {
    const result = await deleteUser(userToChangeId);
    if(!result){
      messageApi.open({
        type: "error",
        content: "User was not deleted, try again!",
      }); 
    } else {
      messageApi.open({
        type: "success",
        content: "User was deleted successfully!",
      }); 
    }
  };

  const showInviteOrDeleteModal = (purpose) => {
    setIsModalOpen(false);
    setModalPurpose(purpose);
    setIsInviteOrDeleteModalOpen(true);
  };

  const closeInviteOrDeleteModal = () => {
    setUserToChangeId(null);
    setIsInviteOrDeleteModalOpen(false);
  };

  const handleDeleteTeammate = () => {
    deleteTeammate();
    setUserToChangeId(null);
    closeInviteOrDeleteModal();
  };

  const handleRole = (role) => {
    setRole(role);
  };

  const getTeamCreatedDate = () => {
    let minTimestamp = Number.MAX_SAFE_INTEGER;
    teammates.forEach((teammate) =>{
      minTimestamp = Math.min(minTimestamp, teammate.createdTimestamp);
    })
    if(minTimestamp !== Number.MAX_SAFE_INTEGER){
      const date = new Date(minTimestamp);

      const year = date.getFullYear();
      const month = date.getMonth() + 1
      const day = date.getDate();

      setDateCreated(day.toString() + '.' + month.toString() + '.' + year.toString());
    }
  }

  const getRoleFromUser = (teammate) => {
    if(!teammate?.roles || !teammate) return "";
    for (const role of teammate.roles) {
      if (options.some((option) => option.value === role.name)) {
        return role.name;
      }
    };
    return "";
  }

  const getRoleInfo = (role) => {
    const selectedOption = options.find(option => option.value === role);
    return selectedOption ? selectedOption.text : 'Role information not found';
  }

  const getFilteredOptions = () => {
    if(!personRole){
      setPersonRole(getRoleFromUser(person));
    }
    const userRoleLevel = roleHierarchy[personRole];
    if (userRoleLevel === undefined) {
      return [];
    }
  
    const filteredOptions = [];
    for (const option of options) {
      const optionRoleLevel = roleHierarchy[option.value];
  
      if (optionRoleLevel !== undefined && optionRoleLevel <= userRoleLevel) {
        filteredOptions.push(option);
      }
    }
    setFilteredOptions(filteredOptions);
  }

  useEffect(()=>{
    getFilteredOptions();
    setPersonRole(getRoleFromUser(person));
  },[person]);

  useEffect(()=>{
    getTeamCreatedDate();
    countRoles();
  }, [teammates]);

  const getTeam = () => {
    return teammates?.map((teammate) => {
      const firstName = teammate.firstName == undefined || teammate.firstName == 'undefined' ? " " : teammate.firstName;
      const lastName = teammate.lastName == undefined || teammate.lastName == 'undefined' ? " " : teammate.lastName;
      const name = (firstName.trim() + " " + (lastName ? lastName.trim() : "")).trim();
      let teammateRole = null; 

      teammateRole = getRoleFromUser(teammate);
      const roleInfo = getRoleInfo(teammateRole);
      if(!personRole){
        setPersonRole(getRoleFromUser(person));
      }
      
      return (
        <div
          key={teammate.id}
          className="teammate-card"
        >
          {person == teammate || personRole === 'super-admin' || roleHierarchy[personRole] > roleHierarchy[teammateRole] ? 
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography.Title level={4}>{name}</Typography.Title>
            <EditOutlined onClick={() => showModal("edit", teammate)} />
          </div> : <Typography.Title level={4}>{name}</Typography.Title>}
          <div>{teammate?.attributes?.position ? teammate.attributes.position[0] : <br/>}</div>
          <div style={{ color: "#1BC200" }}>
            {name === personKeycloak.idTokenParsed.name ? "That's you" : <br />}
          </div>
          <Typography.Title level={4}>
            Role:{" "}
            <span style={{ color: "#FF0000", fontSize: "14px" }}>
              {teammateRole.length > 2 ? teammateRole.charAt(0).toUpperCase() + teammateRole.slice(1) : ""}
            </span>
          </Typography.Title>
          <div>
            {roleInfo}
          </div>
        </div>
      );
    });
  };

  return (
    <>
    {resultMessage}
    <Skeleton loading={isLoading}>
      <div>
        <Typography.Title level={3}>
          Team
        </Typography.Title>
        <div
          className="team-stats"
        >
          <div className="graph-container">
            <TeamActivityGraph roles={rolesCount}/>
          </div>
          <div
            className="team-info"
            style={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#D9D9D9",
              padding: "50px",
              borderRadius: "25px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                columnGap: "10px",
              }}
            >
              <UserOutlined style={{ fontSize: "3em" }} />
              <div>
                <div>Your Veriteam:</div>
                <div>Team created {dateCreated}</div>
              </div>
              <div className="teammates-number" style={{ fontSize: "4em", fontWeight: "bold" }}>
                {teammates?.length}
              </div>
            </div>

            <div
              style={{
                maxWidth: "220px",
                marginBottom: "20px",
              }}
            >
              Add your teammates to get the best experience working with
              Verigram Console!
            </div>
            <div>
              <PlusCircleOutlined
              className="add-teammate-button"
                onClick={() =>
                  showModal("add", { firstName: "", lastName: "", position: "", email: "" })
                }
                style={{
                  fontSize: "2em",
                  verticalAlign: "middle",
                }}
              />
              <Typography.Text> Add Teammate</Typography.Text>
            </div>
          </div>
        </div>
        <div
          className="team"
          style={{
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "row",
            rowGap: "20px",
            columnGap: "20px",
            marginTop: "20px",
          }}
        >
          {getTeam()}
        </div>
      </div>
      <Modal
        title=""
        open={isModalOpen}
        footer={null}
        width={1000}
        onCancel={(e) => {
          setIsModalOpen(false);
        }}
      >
        <Typography.Title level={2}>
          {modalMode === "add"
            ? "Add Teammate"
            : modalMode === "edit"
            ? "Edit Teammate"
            : ""}
        </Typography.Title>
        <div style={{ display: "flex", columnGap: "30px", marginTop: "50px" }}>
          <div
            style={{
              width: "50%",
            }}
          >
            <FloatLabel label="Name" name="Name" value={fullName}>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </FloatLabel>
            <FloatLabel label="Position" name="Position" value={position}>
              <Input
                value={position}
                onChange={(e) => setPosition(e.target.value)}
              />
            </FloatLabel>
            <FloatLabel label="E-Mail" name="email" value={email}>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </FloatLabel>
            <FloatLabel label="Role" name="role" value={role}>
              <Select
                style={{ width: "100%" }}
                value={role}
                onChange={handleRole}
                options={filteredOptions}
              />
            </FloatLabel>
          </div>
          <div
            style={{
              width: "50%",
            }}
          >
            <Typography.Title level={4}>
              What is the difference among the roles?
            </Typography.Title>
            <div
              style={{
                fontSize: "14px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography.Text>
                There are different levels of security inside of Verigram
                Console. We have made this feature to provide extra-safe
                experience for our customers.
              </Typography.Text>
              <Typography.Text>
                Super Admin - This is a role for an IT-engineer or another
                person, who implements the technology into his platform
              </Typography.Text>
              <Typography.Text>
                Admin - This is a role for a Head of the Department, which needs
                the technology.
              </Typography.Text>
              <Typography.Text>
                Operator - this is a role for an assistant that does not have
                access to Settings and Flows. Significant Actions such as Manual
                Review or adding a person into a blacklist requires a
                confirmation from Super Admin or Admin{" "}
              </Typography.Text>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "60px",
          }}
        >
          {modalMode === "add" ? (
            <Button
              onClick={addTeammate}
              disabled={!role || !fullName || !email || !position || !validateEmail(email)}
              className="send-invite-btn"
            >
              Send Invite
            </Button>
          ) : (
            <div style={{ display: "flex", columnGap: "40px" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "#1BC200",
                  borderColor: "#1BC200",
                  borderRadius: "25px",
                }}
                onClick={handleUpdate}
                className="confirm-changes-btn"
              >
                Confirm Changes
              </Button>
              <Button
                onClick={() => showInviteOrDeleteModal("delete")}
                type="primary"
                style={{
                  backgroundColor: "#FF0000",
                  borderColor: "#FF0000",
                  borderRadius: "25px",
                }}
                hidden={person?.id === userToChangeId}
                className="delete-teammate-btn"
              >
                Delete Teammate
              </Button>
            </div>
          )}
        </div>
      </Modal>
      <Modal
        open={isInviteOrDeleteModalOpen}
        centered
        footer={null}
        onCancel={(e) => {
          setIsInviteOrDeleteModalOpen(false);
        }}
      >
        {modalPurpose === "delete" ? (
          <>
            <Typography.Title level={2}>Are you sure? </Typography.Title>
            <div>
              You are trying to delete your teammate. <br />
              Are you sure you want to do it? <br />
              This action cannot be undone.
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                columnGap: "30px",
                marginTop: "30px",
              }}
            >
              <Button
                onClick={handleDeleteTeammate}
                type="primary"
                style={{
                  backgroundColor: "#1BC200",
                  borderColor: "#1BC200",
                  borderRadius: "25px",
                }}
              >
                Accept
              </Button>
              <Button
                onClick={closeInviteOrDeleteModal}
                type="primary"
                style={{
                  backgroundColor: "#FF0000",
                  borderColor: "#FF0000",
                  borderRadius: "25px",
                }}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <Typography.Title level={2}>Thank you! </Typography.Title>
            <div>
              Your invite was successfully sent to{" "}
              <span style={{ fontWeight: "600" }}>{fullName}!</span>
            </div>
            <div>
              Please ask him to check his e-mail{" "}
              <span style={{ fontWeight: "600" }}>{email}</span> to start
              working in Verigram Console together!
            </div>
          </>
        )}
      </Modal>
      </Skeleton>
    </>
  );
}
